import { useLayoutEffect, useRef, useState, type RefObject } from 'react';
import { isNumber } from 'lodash/fp';
import {
    arrow as arrowMiddleware,
    autoUpdate,
    flip as flipMiddleware,
    FloatingArrow,
    offset as offsetMiddleware,
    type Placement,
    shift as shiftMiddleware,
    useFloating,
    type UseFloatingReturn,
    type FloatingContext
} from '@floating-ui/react';
import {
    createVirtualElement,
    resolveReferenceElement,
    type ReferenceElement
} from 'app/lib/utils';
import type { Position } from 'app/lib/types/ui';
import * as styles from './Modal.css';

const ARROW_HEIGHT = 7;
const GAP = 2;

interface ArrowProps {
    context: FloatingContext;
    ref: RefObject<SVGSVGElement | null>;
}

function Arrow({ context, ref }: ArrowProps) {
    return (
        <FloatingArrow
            className={styles.arrow}
            ref={ref}
            context={context}
            d="M0,14 L6.125,7.825 Q7,7,7.825,7.825 L14,14"
            strokeWidth={1}
        />
    );
}

function isPositionObject(obj: unknown): obj is Position {
    return (
        typeof obj === 'object' &&
        obj !== null &&
        isNumber((obj as Record<string, unknown>).x) &&
        isNumber((obj as Record<string, unknown>).y)
    );
}

interface UseAttachedModalLayoutOptions {
    placement?: Placement;
    attachTo?: ReferenceElement | Position;
    offset?: number;
}

interface UseAttachedModalLayoutReturn {
    Arrow: typeof Arrow;
    context: FloatingContext;
    elements: UseFloatingReturn['elements'];
    refs: UseFloatingReturn['refs'] & {
        arrow: RefObject<SVGSVGElement | null>;
    };
    floatingStyles: UseFloatingReturn['floatingStyles'];
}

export function useAttachedModalLayout(
    options: UseAttachedModalLayoutOptions = {}
): UseAttachedModalLayoutReturn {
    const { placement, attachTo, offset = ARROW_HEIGHT + GAP } = options;
    const arrowRef = useRef<SVGSVGElement>(null);
    const [referenceElement, setReferenceElement] = useState<Element | null>();

    const floatingUiParams = {
        elements: {
            reference: referenceElement
        },
        placement,
        whileElementsMounted: autoUpdate,
        middleware: [
            offsetMiddleware(offset),
            flipMiddleware(),
            shiftMiddleware({ padding: 16 }),
            arrowMiddleware({ element: arrowRef })
        ]
    };

    const { context, elements, refs, floatingStyles } = useFloating(floatingUiParams);

    useLayoutEffect(() => {
        setReferenceElement(resolveReferenceElement(attachTo as ReferenceElement));
        if (isPositionObject(attachTo)) {
            refs.setPositionReference(createVirtualElement(attachTo));
        }
    }, [refs, attachTo]);

    return {
        Arrow,
        context,
        elements,
        refs: {
            ...refs,
            arrow: arrowRef
        },
        floatingStyles
    };
}
